/**
 * Which docs SITE a given user or URL belongs to.
 *
 * There are two docs sites and they are two sites, not one corpus with a flag
 * (working-conventions §43): `/docs` serves the customer artifact, `/internal/docs` serves the
 * staff one. Anything outside the docs pages that needs to reason about "the docs" — the Help
 * menu, the command palette — has to answer two questions, and both were previously answered
 * inline, wrongly, in more than one place:
 *
 *   1. WHICH docs index does this user want?  → `useDocsIndexPath`
 *   2. IS the user already inside docs?       → `isDocsPath`
 *
 * ## Why this module exists rather than a `startsWith(ROUTES.DOCS)` at each call site
 *
 * That is exactly what `SidebarHelpPopover` did, and it produced the mangled back bar this
 * module fixes. `'/internal/docs/internal/sms-twilio-gcp-setup'.startsWith('/docs')` is FALSE,
 * so the staff docs site was not recognised as docs at all: opening Help → Documentation from
 * an internal guide stashed that guide's URL as the referrer, and `useReferrerState` derived
 * the label by title-casing the last path segment — giving "← Back to Sms Twilio Gcp Setup" on
 * the page you landed on. The name was mangled because a SLUG is not a title; the link was
 * there at all because the surface check missed half the surface.
 *
 * Both bugs are one missing predicate, so it lives here once and every caller reads it.
 *
 * ## Import boundary
 *
 * This module imports NO docs artifact — not the customer manifest, not the internal one. Its
 * importers include eagerly-routed chrome (the sidebar), and `docs/sources.internal.ts`
 * documents at length why internal guide text must never reach that graph. Keep it that way:
 * route strings and a role predicate only.
 */
import { ROUTES } from 'routes'

import { useIsInternalUser } from 'hooks/useIsInternalUser'

/**
 * What each docs site is CALLED.
 *
 * They live here, beside the route predicates, because two kinds of module need them and only
 * one of those may touch an artifact. `docs/sources.ts` and `docs/sources.internal.ts` set them
 * on their `DocsSource`; the ⌘K palette needs the same two words for its result-group headings
 * and must not import either sources module to get them — importing `sources.internal.ts` from
 * eagerly-routed chrome is precisely the leak that file's header exists to prevent.
 *
 * "Internal docs" rather than "Docs": a staffer holds both surfaces at once — in the sidebar,
 * and now in one search result list — and two destinations both reading "Docs" is the kind of
 * ambiguity that gets an internal runbook link pasted into a customer conversation.
 */
export const CUSTOMER_DOCS_LABEL = 'Docs'
export const INTERNAL_DOCS_LABEL = 'Internal docs'

const segments = (path: string) => path.split('?')[0].split('/').filter(Boolean)

/**
 * Is `path` the docs index of either site, or a page beneath it?
 *
 * Compared SEGMENT by segment rather than by string prefix, for the reason `isAncestorPath`
 * gives in `@repo/ui`: a prefix test says `/docs` contains `/docs-archive`, which is a
 * different surface that merely starts with the same letters.
 */
export const isDocsPath = (path: string): boolean => {
  const parts = segments(path)
  return [ROUTES.DOCS, ROUTES.INTERNAL_DOCS].some(root => {
    const rootParts = segments(root)
    return rootParts.every((seg, i) => parts[i] === seg)
  })
}

/**
 * The docs index this user should land on: the staff site for Vera staff, the customer site
 * for everyone else.
 *
 * `useIsInternalUser` carries a `!inSpace` clause — a staffer operating inside a brand's space
 * is treated as that brand's user (VERA-876). That is deliberate and is respected here: while
 * you are acting as a brand, you want the brand's documentation, which is the same reason the
 * internal nav is hidden in that state.
 */
export const useDocsIndexPath = (): string => {
  const isInternal = useIsInternalUser()
  return isInternal ? ROUTES.INTERNAL_DOCS : ROUTES.DOCS
}
